import gql from "graphql-tag";
import { groupBy, startCase } from "lodash-es";
import moment from "moment-timezone";
import { useHistory, useLocation } from "react-router-dom";
import { BorderButton } from "src/components/buttons/BorderButton";
import FilterButton from "src/components/buttons/FilterButton";
import { InputPopover } from "src/components/inputs/InputPopover";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import {
  OrganizationFiltersKeys,
  days as allDays,
  dateFormat,
} from "src/utils/constants";
import formatMoney from "src/utils/formatMoney";
import tw from "src/utils/tw";
import { useMediaQueries } from "src/utils/useMediaQueries";
import { filterParams, useSearchParams } from "src/utils/useSearchParams";
import {
  OfferingStatus,
  findFilters,
  findFiltersVariables,
} from "types/code-generator";
import { TypedDocumentNode } from "types/graphql";

interface Props {
  resultsOnly?: boolean;
  setFilterOpen?: () => void;
}

export function FindFilters(props: Props) {
  const store = useStore();
  const params = useSearchParams();
  const history = useHistory();
  const location = useLocation();
  const { small } = useMediaQueries();

  let date = moment();

  if (store.isAdminArea) {
    date = date.add(99, "years");
  }

  const queryFilters = useApi(FindFiltersQuery, {
    variables: {
      filter: location.search,
      groupWhere: {
        organizationId: store.organization.id,
        status: { in: [OfferingStatus.InProgress, OfferingStatus.Upcoming] },
        AND: store.isAdminArea
          ? undefined
          : [
              {
                OR: [
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: { gt: date.toDate() },
                  },
                  { unpublishDate: { gt: date.toDate() } },
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: null,
                  },
                ],
              },
            ],
      },
      privateWhere: {
        organizationId: store.organization.id,
        template: true,
        status: { equals: OfferingStatus.InProgress },
        AND: store.isAdminArea
          ? undefined
          : [
              {
                OR: [
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: { gt: date.toDate() },
                  },
                  { unpublishDate: { gt: date.toDate() } },
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: null,
                  },
                ],
              },
            ],
      },
      membershipWhere: {
        organizationId: store.organization.id,
        status: { in: [OfferingStatus.InProgress, OfferingStatus.Upcoming] },
        AND: store.isAdminArea
          ? undefined
          : [
              {
                OR: [
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: { gt: date.toDate() },
                  },
                  { unpublishDate: { gt: date.toDate() } },
                  {
                    publishDate: { lt: date.toDate() },
                    unpublishDate: null,
                  },
                ],
              },
            ],
      },
    },
  });

  const departments = store.organization.departments.map((d) => ({
    id: d.id,
    title: d.title,
  }));

  let locations = [];
  let zones = [];
  let tags = [];
  let instructors = [];
  let ages = [];
  let days = [];
  let programs = [];

  if (queryFilters.findFilters) {
    locations = queryFilters.findFilters.locations || [];
    zones = queryFilters.findFilters.zones || [];
    tags = queryFilters.findFilters.tags || [];
    instructors = queryFilters.findFilters.instructors || [];
    ages = queryFilters.findFilters.ages || [];
    days = queryFilters.findFilters.days || [];
    programs = queryFilters.findFilters.programs || [];
  }

  const allFiltersData = {
    department: {
      label: "Department",
      data: store.organization.hasDepartments ? departments : [],
    },
    program: {
      label: "Program",
      data: programs,
    },
    location: {
      label: "Location",
      data: locations,
    },
    zone: {
      label: "Zone",
      data: zones,
    },
    instructor: {
      label: store.organization.terminology.staff,
      data: instructors,
    },
    ages: {
      label: "Age",
      data: ages.map((a) => ({
        id: a,
        title: a,
      })),
    },
    day: {
      label: "Day",
      data: days.map((d) => {
        const day = allDays.find((day) => day.value === d);
        return {
          id: day.value,
          title: day.short,
        };
      }),
    },
    tag: {
      label: "Tag",
      data: tags,
    },
  };

  const filtersData = {};

  if (store.organization.hasDepartments) {
    filtersData["department"] = allFiltersData["department"];
  }

  const publicFilters = store.organization.departments.reduce((acc, d) => {
    for (const f of d.filters) {
      if (acc.includes(f)) {
        continue;
      }

      acc.push(f);
    }

    return acc;
  }, []);

  const publicFilterKeys = publicFilters.map((f) => OrganizationFiltersKeys[f]);

  for (const f of publicFilterKeys) {
    filtersData[f] = allFiltersData[f];
  }

  for (const k of params.keys()) {
    if (Object.keys(filtersData).includes(k)) {
      continue;
    }

    if (
      k.includes("Type") ||
      k.includes("Between") ||
      k.includes("Exact") ||
      k === "filter" ||
      k === "order" ||
      k === "returnPathname" ||
      k === "fbclid"
    ) {
      continue;
    }

    filtersData[k] = {
      label: startCase(k),
      data: params.getAll(k),
    };
  }

  if (Object.keys(filtersData).length === 0) {
    return null;
  }

  function removeFilter(filter) {
    if (filter === "more") {
      params.delete("program");
      params.delete("tag");
      params.delete("hide");
    } else if (filter === "location") {
      params.delete("location");
      params.delete("zone");
    } else {
      params.delete(filter);
    }

    history.replace(`?${params.toString()}`);
  }

  // console.log("zones", zones);

  function renderFilterGroup(key: string) {
    const filter = filtersData[key];
    const summary = getSummary(key);

    // console.log("key", key);

    if (filter.data?.length === 0) {
      return null;
    }

    if (!publicFilterKeys.includes(key) && !props.resultsOnly) {
      return null;
    }

    if (publicFilterKeys.includes(key) && props.resultsOnly) {
      return null;
    }

    // console.log("filter", filter);

    let label = filter.label;

    if (label === "Max Enrollment") {
      label = "Capacity";
    }

    let buttons = (
      <>
        {filter.data?.map((p) => {
          return (
            <BorderButton
              key={p.id}
              xSmall
              onClick={() => {
                const newParams = filterParams({
                  key,
                  value: p.id,
                });

                history.replace(`?${newParams.toString()}`);
              }}
              className={tw(
                "truncate text-sm",
                params.getAll(key).includes(p.id.toString()) &&
                  "border-organization"
              )}
            >
              <span className="text-organization">{p.title}</span>
            </BorderButton>
          );
        })}

        {params.has(key) && (
          <BorderButton
            xSmall
            onClick={() => removeFilter(key)}
            className={tw(key === "location" && "w-full")}
          >
            <div className="text-sm text-organization">All</div>
          </BorderButton>
        )}
      </>
    );

    if (key === "location") {
      buttons = (
        <>
          {filter.data.map((p) => {
            const locationZones = zones.filter((z) => z.locationId === p.id);

            return (
              <div key={p.id}>
                <BorderButton
                  xSmall
                  onClick={() => {
                    const newParams = filterParams({
                      key,
                      value: p.id,
                    });

                    history.push(`?${newParams.toString()}`);
                  }}
                  className={tw(
                    "w-full truncate pl-4 text-left text-sm",
                    params.getAll(key).includes(p.id) && "border-organization"
                  )}
                >
                  <div className="text-sm text-organization">{p.title}</div>
                </BorderButton>

                <div className="mb-2">
                  {locationZones.map((z) => {
                    return (
                      <BorderButton
                        key={z.id}
                        xSmall
                        onClick={() => {
                          const newParams = filterParams({
                            key: "zone",
                            value: z.id,
                          });

                          history.push(`?${newParams.toString()}`);
                        }}
                        className={tw(
                          "truncate text-sm",
                          params.getAll("zone").includes(z.id) &&
                            "border-organization"
                        )}
                      >
                        <div className="text-sm text-organization">
                          {z.title}
                        </div>
                      </BorderButton>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {params.has(key) && (
            <BorderButton
              xSmall
              onClick={() => removeFilter(key)}
              className={tw(key === "location" && "w-full")}
            >
              <div className="text-sm text-organization">All</div>
            </BorderButton>
          )}
        </>
      );
    }

    if (key === "program" && store.organization.hasDepartments) {
      const groupedPrograms = groupBy(programs, "department.id");

      buttons = (
        <>
          {Object.keys(groupedPrograms).map((k) => {
            const department = departments.find((d) => d.id === k);

            return (
              <div key={k}>
                <div className="mb-2">
                  <div className="step-subhead text-xs">
                    {department?.title}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {groupedPrograms[k].map((p) => {
                    return (
                      <BorderButton
                        key={p.id}
                        xSmall
                        onClick={() => {
                          const newParams = filterParams({
                            key,
                            value: p.id,
                          });

                          history.push(`?${newParams.toString()}`);
                        }}
                        className={tw(
                          "truncate text-sm",
                          params.getAll(key).includes(p.id) &&
                            "border-organization"
                        )}
                      >
                        <span className="text-organization">{p.title}</span>
                      </BorderButton>
                    );
                  })}

                  {params.has(key) && (
                    <BorderButton xSmall onClick={() => removeFilter(key)}>
                      <div className="text-sm text-organization">All</div>
                    </BorderButton>
                  )}
                </div>
              </div>
            );
          })}
        </>
      );
    }

    const popoverButton = (
      <div className="max-w-[140px] p-2 pr-0 sm:px-2 sm:pt-1.5">
        <div className="step-subhead pr-4 sm:hidden">{label}</div>
        <div className="truncate text-sm font-bold text-organization">
          {small ? label : summary}
        </div>
      </div>
    );

    const filterContainer = (
      <InputPopover
        update
        content={
          <div className="p-4">
            <div className="flex flex-wrap gap-2">{buttons}</div>
          </div>
        }
        className="w-full"
        smallFull
      >
        {popoverButton}
      </InputPopover>
    );

    // if (!publicFilterKeys.includes(key) && key !== "department") {
    //   filterContainer = <div>{popoverButton}</div>;
    // }

    return (
      <div
        key={key}
        className={tw(
          "flex h-12 max-w-xs items-center rounded border border-borderGray sm:h-7 sm:rounded-full sm:border-b-3",
          params.has(key) && "sm:border-organization"
        )}
      >
        <div className="max-w-[84%] flex-1 sm:max-w-none">
          {filterContainer}
        </div>

        {params.has(key) && !small && (
          <>
            <div className="p-2">
              <BorderButton round small onClick={() => removeFilter(key)}>
                <i aria-hidden className="far fa-xmark text-organization" />
              </BorderButton>
            </div>
          </>
        )}
      </div>
    );
  }

  function renderResults(key: string) {
    const filter = filtersData[key];
    const values = params.getAll(key);

    if (filter.data?.length === 0) {
      return null;
    }

    if (!params.has(key)) {
      return null;
    }

    return values.map((s) => {
      const summary = getSummary(key, s);

      return (
        <div
          key={s}
          className="flex h-7 max-w-xs items-center rounded-full bg-organization pl-2.5 text-white"
        >
          <div className="max-w-[84%] truncate text-sm font-bold">
            {summary}
          </div>

          {params.has(key) && (
            <>
              <button
                onClick={() => {
                  const newParams = filterParams({
                    key,
                    value: s,
                  });

                  history.replace(`?${newParams.toString()}`);
                }}
                className="pl-2 pr-2.5"
              >
                <i
                  aria-hidden
                  className={tw(
                    "far fa-xmark text-empty",
                    params.has(key) && "sm:text-white"
                  )}
                />
              </button>
            </>
          )}
        </div>
      );
    });
  }

  function getSummary(key: string, value: string = null) {
    let selected = params.getAll(key);

    if (value) {
      selected = [value];
    }

    let summary =
      selected.length > 0
        ? selected
            .map((n) => startCase(n))
            .sort()
            .join(", ")
        : "All";

    if (selected.length === 0) {
      return summary;
    }

    if (key === "department") {
      summary = selected
        .map((u) => {
          const item = departments.find((p) => p.id === u);

          return item?.title;
        })
        .join(", ");
    }

    if (key === "program") {
      summary = selected
        .map((u) => {
          const item = programs.find((p) => p.id === u);

          return item?.title;
        })
        .join(", ");
    }

    if (key === "tag") {
      summary = selected
        .map((u) => {
          const item = tags.find((p) => p.id === u);

          return item?.title;
        })
        .join(", ");
    }

    if (key === "time") {
      const selectedTimes = selected.map((s) =>
        moment(s, "HH:mm").format("h:mm a")
      );

      summary = selectedTimes.join(", ");
    }

    if (key === "instructor") {
      const selectedUsers = selected.map((s) => {
        const user = instructors.find((i) => i.id === s);

        return user ? user.title : "";
      });

      summary = selectedUsers.join(", ");
    }

    if (key === "location") {
      const selectedLocations = selected.map((s) => {
        const location = locations.find((i) => i.id === s);

        if (location) {
          return location.title;
        }

        return "";
      });

      summary = selectedLocations.join(", ");
    }

    if (key === "zone") {
      const selectedZones = selected.map((s) => {
        const zone = zones.find((i) => i.id === s);

        return zone?.title;
      });

      summary = selectedZones.join(", ");
    }

    if (key === "startDate") {
      summary = renderDateSummary("startDate");
    }

    if (key === "endDate") {
      summary = renderDateSummary("endDate");
    }

    if (key === "day") {
      summary = selected
        .map((n) => {
          const day = allDays.find((day) => day.value === n);

          return day.short;
        })
        .sort()
        .join(", ");
      if (params.get("dayExact") === "true") {
        summary = `Only ${summary}`;
      }
    }

    if (key === "startTime") {
      summary = renderTimeSummary("startTime");
    }

    if (key === "endTime") {
      summary = renderTimeSummary("endTime");
    }

    if (key === "ages") {
      const ages = selected.map((a) => {
        if (a === "0") {
          return "< 1";
        }

        if (a === "99") {
          return "20+";
        }

        return a;
      });

      summary = `Age ${ages.join(", ")}`;
    }

    if (key === "price") {
      const price = parseInt(params.get("price"));
      const priceType = params.get("priceType");
      const priceBetween =
        params.has("priceBetween") && parseInt(params.get("priceBetween"));

      summary = `$${formatMoney(price)}`;

      if (priceType === "between" && priceBetween) {
        summary = `$${formatMoney(price)}-$${formatMoney(priceBetween)}`;
      }

      if (priceType === "under") {
        summary = `≤ $${formatMoney(price)}`;
      }

      if (priceType === "over") {
        summary = `≥ $${formatMoney(price)}`;
      }
    }

    if (key === "maxEnrollment") {
      summary = `Capacity ${params.get("maxEnrollment")}`;
    }

    return summary;
  }

  function renderDateSummary(key: string) {
    const date = moment(params.get(key));
    const dateType = params.get(`${key}Type`);
    const dateBetween =
      params.get(`${key}Between`) && moment(params.get(`${key}Between`));

    let summary = `Start ${date.format(dateFormat)}`;

    if (dateType) {
      if (dateBetween && dateType === "between") {
        summary = `Start ${date.format(dateFormat)}-${dateBetween.format(
          dateFormat
        )}`;
      }

      if (dateType === "after") {
        summary = `Start > ${date.format(dateFormat)}`;
      }

      if (dateType === "before") {
        summary = `Start < ${date.format(dateFormat)}`;
      }
    }

    return summary;
  }

  function renderTimeSummary(key: string) {
    const time = moment(params.get(key), "HH:mm");
    const timeType = params.get(`${key}Type`);
    const timeBetween =
      params.has(`${key}Between`) &&
      moment(params.get(`${key}Between`), "HH:mm");

    let summary = `Starts ${time.format("h:mm a")}`;

    if (timeType) {
      if (timeBetween && timeType === "between") {
        summary = `Start ${time.format("h:mm a")}-${timeBetween.format(
          "h:mm a"
        )}`;
      }

      if (timeType === "after") {
        summary = `Start > ${time.format("h:mm a")}`;
      }

      if (timeType === "before") {
        summary = `Start < ${time.format("h:mm a")}`;
      }
    }

    return summary;
  }

  function clearParams() {
    history.replace(location.pathname);
  }

  // console.log("filtersData", filtersData);
  // console.log("publicFilters", publicFilters);

  return (
    <div>
      {!props.resultsOnly && (
        <div className="flex items-center">
          <div className="flex flex-1 items-center gap-2 overflow-auto py-2 sm:px-4">
            {Object.keys(filtersData).map((key) => {
              return renderFilterGroup(key);
            })}
            <div>&nbsp;</div>
          </div>
          {publicFilters.length > 0 && small && (
            <div className="border-l border-borderGray py-1 pl-2 pr-4">
              <FilterButton onClick={props.setFilterOpen} small round>
                <i aria-hidden className="far fa-filter text-sm" />
              </FilterButton>
            </div>
          )}
        </div>
      )}

      {props.resultsOnly && small && (
        <div className="flex items-center gap-2 overflow-auto px-4">
          {[...params.keys()].length > 0 && (
            <div className="flex h-7 items-center rounded-full border-none bg-borderGray pl-2.5 text-sm leading-tight text-empty">
              <div>
                <strong>Clear</strong>
              </div>
              <button onClick={clearParams} className="pl-2 pr-2.5">
                <i className="far fa-xmark" />
              </button>
            </div>
          )}

          {Object.keys(filtersData).map((key) => {
            return renderResults(key);
          })}

          <div>&nbsp;</div>
        </div>
      )}
    </div>
  );
}

export const FindFiltersQuery = gql`
  query findFilters(
    $groupWhere: ClassWhereInput
    $privateWhere: OfferingWhereInput
    $membershipWhere: MembershipOfferingWhereInput
    $filter: String
  ) {
    findFilters(
      groupWhere: $groupWhere
      privateWhere: $privateWhere
      membershipWhere: $membershipWhere
      filter: $filter
    ) {
      departments {
        id
        title
      }
      programs {
        id
        title
      }
      times
      locations {
        id
        title
        zones {
          id
          title
        }
      }
      zones {
        id
        title
      }
      tags {
        id
        title
      }
      instructors {
        id
        title
      }
      days
      ages
    }
  }
` as TypedDocumentNode<findFilters, findFiltersVariables>;
